import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Mic, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  claimWeeklySlot,
  releaseSpeakerSlot,
  updateSpeakerTopic,
  useWeeklySpeakers,
  speakerKey,
} from "@/hooks/useWeeklySpeakers";
import { useCurrentMemberId } from "@/hooks/useEventRsvp";
import SpeakerCard from "@/components/SpeakerCard";

interface Props {
  chapter: "washington" | "union";
  occurrenceDate: string; // YYYY-MM-DD
  compact?: boolean;
  timeLabel?: string;
  locationLabel?: string;
}

const WeeklySpeakerSection = ({ chapter, occurrenceDate, compact = false, timeLabel, locationLabel }: Props) => {
  const { speakers, refetch } = useWeeklySpeakers(chapter);
  const { memberId } = useCurrentMemberId();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthed(!!session);
      setAuthChecked(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setIsAuthed(!!s);
    });
    return () => subscription.unsubscribe();
  }, []);

  const slot = speakers[speakerKey(chapter, occurrenceDate)];
  const isMine = slot && memberId && slot.member_id === memberId;

  // Date label e.g. "Wednesday, May 21st"
  let dateLabel: string | undefined;
  try {
    dateLabel = format(parseISO(occurrenceDate), "EEEE, MMMM do");
  } catch {
    dateLabel = undefined;
  }

  const wrapperClass = compact ? "" : "mb-3";

  const handleClaim = async () => {
    if (!memberId) {
      toast.error("You must be a logged-in member to sign up.");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await claimWeeklySlot({
        chapter,
        occurrenceDate,
        memberId,
        topic: editingTopic.trim() || null,
      });
      if (error) {
        if ((error as any).code === "23505") {
          toast.error("Someone just claimed this date. Please refresh.");
        } else {
          toast.error(error.message || "Could not claim slot.");
        }
      } else {
        toast.success("You're signed up to speak!");
        setOpen(false);
        setEditingTopic("");
        refetch();
      }
    } catch (e: any) {
      console.error("claimWeeklySlot threw:", e);
      toast.error(e?.message || "Could not claim slot.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTopic = async () => {
    if (!slot) return;
    setSubmitting(true);
    try {
      const { error } = await updateSpeakerTopic(slot.id, editingTopic.trim() || null);
      if (error) {
        toast.error(error.message || "Could not update topic.");
      } else {
        toast.success("Topic updated.");
        setOpen(false);
        refetch();
      }
    } catch (e: any) {
      console.error("updateSpeakerTopic threw:", e);
      toast.error(e?.message || "Could not update topic.");
    } finally {
      setSubmitting(false);
    }
  };


  const handleRelease = async () => {
    if (!slot) return;
    if (!confirm("Release your speaker slot for this date?")) return;
    const { error } = await releaseSpeakerSlot(slot.id);
    if (error) {
      toast.error(error.message || "Could not release slot.");
    } else {
      toast.success("Slot released.");
      refetch();
    }
  };

  // --- Render: filled slot ---
  if (slot) {
    return (
      <div className={wrapperClass}>
        <SpeakerCard
          chapter={chapter}
          slotType={slot.slot_type}
          member={slot.member ?? null}
          topic={slot.topic}
          dateLabel={dateLabel}
          timeLabel={timeLabel}
          locationLabel={locationLabel}
        >
          {isMine && slot.slot_type === "member" && (
            <div className="flex gap-2">
              <Dialog
                open={open}
                onOpenChange={(v) => {
                  setOpen(v);
                  if (v) setEditingTopic(slot.topic || "");
                }}
              >
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="h-8">
                    <Pencil className="w-3 h-3 mr-1" />
                    Edit Topic
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit your topic</DialogTitle>
                    <DialogDescription>
                      Update what you'll be presenting on this date.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2">
                    <Label htmlFor="topic">Topic</Label>
                    <Textarea
                      id="topic"
                      rows={3}
                      value={editingTopic}
                      onChange={(e) => setEditingTopic(e.target.value)}
                      placeholder="e.g. How we doubled our referrals in 6 months"
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdateTopic} disabled={submitting}>Save</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-destructive hover:text-destructive"
                onClick={handleRelease}
              >
                <X className="w-3 h-3 mr-1" />
                Release
              </Button>
            </div>
          )}
        </SpeakerCard>
      </div>
    );
  }

  // --- Render: open slot ---
  if (!authChecked) return null;

  const openCard = (
    <SpeakerCard chapter={chapter} slotType="open" dateLabel={dateLabel}>
      {isAuthed && memberId ? (
        <Dialog
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (v) setEditingTopic("");
          }}
        >
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full">
              <Mic className="w-4 h-4 mr-2" />
              Sign up to speak
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Sign up as speaker</DialogTitle>
              <DialogDescription>
                You'll be listed as the presenter for this meeting. Topic is optional and can be added later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="claim-topic">Topic (optional)</Label>
              <Textarea
                id="claim-topic"
                rows={3}
                value={editingTopic}
                onChange={(e) => setEditingTopic(e.target.value)}
                placeholder="e.g. How we doubled our referrals in 6 months"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleClaim} disabled={submitting}>
                {submitting ? "Claiming…" : "Claim this date"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </SpeakerCard>
  );

  return <div className={wrapperClass}>{openCard}</div>;
};

export default WeeklySpeakerSection;
